import { useEffect, useRef } from "react";

// We load Leaflet from CDN to avoid bundler config complexity.
// In a CRA app, you'd: npm install leaflet react-leaflet
// and use <MapContainer> from react-leaflet instead.

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (leafletReady) return cb();
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return; // already loading

  // CSS
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = LEAFLET_CSS;
  document.head.appendChild(link);

  // JS
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = () => {
    leafletReady = true;
    leafletCallbacks.forEach((fn) => fn());
    leafletCallbacks = [];
  };
  document.head.appendChild(script);
}

export default function BusMap({ stops = [], vehicles = [], userLocation, selectedStop, onStopClick }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const stopLayerRef = useRef(null);
  const vehicleLayerRef = useRef(null);
  const userLayerRef = useRef(null);

  // Initialise map once Leaflet is loaded
  useEffect(() => {
    loadLeaflet(() => {
      if (mapRef.current || !containerRef.current) return;
      const L = window.L;

      const map = L.map(containerRef.current, {
        center: [50.3714, -4.1425],
        zoom: 14,
        zoomControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      stopLayerRef.current = L.layerGroup().addTo(map);
      vehicleLayerRef.current = L.layerGroup().addTo(map);
      userLayerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
    });
  }, []);

  // Update stop markers
  useEffect(() => {
    if (!mapRef.current || !stopLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    stopLayerRef.current.clearLayers();

    stops.forEach((stop) => {
      const isSelected = selectedStop?.id === stop.id;
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:${isSelected ? 18 : 13}px;
          height:${isSelected ? 18 : 13}px;
          background:${isSelected ? "#003087" : "#fff"};
          border:2.5px solid #003087;
          border-radius:50%;
          box-shadow:0 1px 4px rgba(0,0,0,0.3);
          transition:all 0.2s;
        "></div>`,
        iconSize: [isSelected ? 18 : 13, isSelected ? 18 : 13],
        iconAnchor: [isSelected ? 9 : 6, isSelected ? 9 : 6],
      });

      const marker = L.marker([stop.lat, stop.lng], { icon })
        .bindPopup(`
          <strong style="font-size:13px;">${stop.name}</strong><br/>
          <span style="font-size:11px;color:#555;">Routes: ${stop.routes.join(", ")}</span><br/>
          ${stop.distanceKm !== undefined ? `<span style="font-size:11px;color:#888;">${(stop.distanceKm * 1000).toFixed(0)}m away</span>` : ""}
        `, { maxWidth: 200 })
        .on("click", () => onStopClick && onStopClick(stop));

      stopLayerRef.current.addLayer(marker);
    });
  }, [stops, selectedStop, onStopClick]);

  // Update live vehicle markers
  useEffect(() => {
    if (!mapRef.current || !vehicleLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    vehicleLayerRef.current.clearLayers();

    vehicles.forEach((v) => {
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:26px; height:26px;
          background:#1D9E75;
          border:2px solid #fff;
          border-radius:6px;
          display:flex; align-items:center; justify-content:center;
          font-size:9px; font-weight:700; color:#fff;
          box-shadow:0 2px 6px rgba(0,0,0,0.4);
          transform:rotate(${v.bearing}deg);
        ">${v.route || "🚌"}</div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });

      L.marker([v.lat, v.lng], { icon })
        .bindPopup(`
          <strong>Route ${v.route}</strong><br/>
          <span style="font-size:11px;">${v.destination || ""}</span><br/>
          <span style="font-size:10px;color:#888;">${v.vehicleRef}</span>
        `)
        .addTo(vehicleLayerRef.current);
    });
  }, [vehicles]);

  // Update user location marker
  useEffect(() => {
    if (!mapRef.current || !userLayerRef.current) return;
    const L = window.L;
    if (!L || !userLocation) return;
    userLayerRef.current.clearLayers();

    const icon = L.divIcon({
      className: "",
      html: `<div style="
        width:16px; height:16px;
        background:#0074D9;
        border:3px solid #fff;
        border-radius:50%;
        box-shadow:0 0 0 4px rgba(0,116,217,0.25);
      "></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    L.marker([userLocation.lat, userLocation.lng], { icon })
      .bindPopup("You are here")
      .addTo(userLayerRef.current);

    mapRef.current.flyTo([userLocation.lat, userLocation.lng], 15, { duration: 1.2 });
  }, [userLocation]);

  // Pan to selected stop
  useEffect(() => {
    if (!mapRef.current || !selectedStop) return;
    mapRef.current.flyTo([selectedStop.lat, selectedStop.lng], 16, { duration: 0.8 });
  }, [selectedStop]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "260px",
        borderRadius: "12px",
        overflow: "hidden",
        border: "0.5px solid var(--color-border-tertiary)",
        marginBottom: "0.75rem",
      }}
    />
  );
}
